import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { agent } from '../../utils/agent';
import config from '../../config';
import { encryptData } from '../../utils/cryptoUtils';

/**
 * Used Vehicle Page Component
 * Displays and manages Used Vehicle details for Used Loan Leads
 *
 * Props:
 * - leadId: The current lead ID
 *
 * Functionality:
 * 1. On page load, checks if a record exists in tbl_used_vehicle for the given lead_id
 * 2. If record exists: Prefills all form fields with existing data
 * 3. If no record exists: Shows empty/default form
 * 4. On Save:
 *    - If used_vehicle_id exists (editing): UPDATES the existing record
 *    - If used_vehicle_id is empty: Backend checks if record exists for lead_id
 *      - If exists: UPDATES the existing record (prevents duplicates)
 *      - If not exists: CREATES new record
 *
 * Backend Logic (cu_used_vehicle API):
 * - Uses used_vehicle_id to determine UPDATE vs INSERT
 * - Always checks for existing records by lead_id to prevent duplicates
 * - Returns used_vehicle_id after creation/update
 *
 * Database Table: tbl_used_vehicle
 * - Primary Key: id
 * - Foreign Key: lead_id (references tbl_lead_basic_info.id)
 * - Unique constraint: Only one active record per lead_id (status=1)
 */
const UsedVehiclePage = ({ leadId }) => {
  console.log('UsedVehiclePage - Received leadId:', leadId);

  const [formUsedData, setformUsedData] = useState({
    used_vehicle_id: '',
    lead_id: leadId,
    // Vehicle Details
    rc_number: '',
    chassis_number: '',
    engine_number: '',
    previous_loan_id: '',
    // Price Details
    price_of_vehicle: '',
    valuation_of_asset: '',
    // Insurance
    insurance_company: '',
    insurance_validity: '',
    // Age
    asset_age: '',
    asset_age_months: '',
    // Running
    vehicle_run_kilometers: '',
    // Inspection
    inspection_date: '',
    inspection_status: 'Pending',
    vehicle_images: [],
    // Source and Payment
    source_by: '',
    payment_to: '',
    // Additional
    vehicle_condition: 'Good',
    remarks: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isExistingRecord, setIsExistingRecord] = useState(false);
  const [selectedImageFiles, setSelectedImageFiles] = useState([]);

  // Source By options
  const sourceByOptions = [
    { value: 'cKers Employee', label: 'cKers Employee' },
    { value: 'Open Market', label: 'Open Market' }
  ];

  // Payment To options
  const paymentToOptions = [
    { value: 'Internal Adjustment', label: 'Internal Adjustment' },
    { value: 'Dealer', label: 'Dealer' },
    { value: 'OEM', label: 'OEM' },
    { value: 'Customer', label: 'Customer' }
  ];

  // Vehicle Condition options
  const conditionOptions = [
    { value: 'Excellent', label: 'Excellent' },
    { value: 'Good', label: 'Good' },
    { value: 'Fair', label: 'Fair' },
    { value: 'Poor', label: 'Poor' }
  ];

  // Inspection Status options
  const inspectionStatusOptions = [
    { value: 'Pending', label: 'Pending' },
    { value: 'Completed', label: 'Completed' },
    { value: 'Not Required', label: 'Not Required' }
  ];

  // Update lead_id in formUsedData when leadId prop changes
  useEffect(() => {
    if (leadId) {
      setformUsedData(prev => ({
        ...prev,
        lead_id: leadId
      }));
    }
  }, [leadId]);

  // Fetch existing used vehicle data
  useEffect(() => {
    if (leadId) {
      fetchUsedVehicleData();
    }
  }, [leadId]);

  const fetchUsedVehicleData = async () => {
    setIsLoading(true);
    console.log('📥 Fetching used vehicle data for lead_id:', leadId);

    try {
      const params = encryptData({ lead_id: leadId });
      const response = await agent.post('get_used_vehicle', params);

      console.log('📦 API Response:', response.data);

      if (response.data && response.data.success && response.data.data && response.data.data.length > 0) {
        const vehicleData = response.data.data[0];
        console.log('✅ Existing record found! ID:', vehicleData.id);
        console.log('📋 Vehicle Data:', vehicleData);

        // Mark as existing record
        setIsExistingRecord(true);

        // Prefill all form fields
        setformUsedData(prevState => ({
          ...prevState,
          lead_id: leadId, // Ensure lead_id is preserved
          used_vehicle_id: vehicleData.id || '',
          rc_number: vehicleData.rc_number || '',
          chassis_number: vehicleData.chassis_number || '',
          engine_number: vehicleData.engine_number || '',
          previous_loan_id: vehicleData.previous_loan_id || '',
          // Numeric fields: use ?? to preserve 0 values
          price_of_vehicle: vehicleData.price_of_vehicle ?? '',
          valuation_of_asset: vehicleData.valuation_of_asset ?? '',
          asset_age: vehicleData.asset_age ?? '',
          asset_age_months: vehicleData.asset_age_months ?? '',
          vehicle_run_kilometers: vehicleData.vehicle_run_kilometers ?? '',
          // String fields
          insurance_company: vehicleData.insurance_company || '',
          insurance_validity: formatDateForInput(vehicleData.insurance_validity) || '',
          inspection_date: formatDateForInput(vehicleData.inspection_date) || '',
          inspection_status: vehicleData.inspection_status || 'Pending',
          vehicle_images: vehicleData.vehicle_images || [],
          source_by: vehicleData.source_by || '',
          payment_to: vehicleData.payment_to || '',
          vehicle_condition: vehicleData.vehicle_condition || 'Good',
          remarks: vehicleData.remarks || ''
        }));

        // toast.info('Existing vehicle details loaded successfully', {
        //   position: "top-right",
        //   autoClose: 2000
        // });
      } else {
        console.log('ℹ️ No existing record found - showing empty form');
        setIsExistingRecord(false);
        // Try to prefill RC/Chassis/Engine numbers from a previous lead with the same mobile number
        fetchVehiclePrefillDetails();
      }
    } catch (error) {
      console.error('❌ Error fetching used vehicle data:', error);
      setIsExistingRecord(false);

      // Show error message only if it's not a "no data found" scenario
      if (error.response && error.response.status !== 404) {
        toast.error('Failed to load used vehicle details. Please refresh the page.', {
          position: "top-right",
          autoClose: 5000
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

    // Fetch prefill vehicle details (RC/Chassis/Engine) from a previous lead with same mobile no
  const fetchVehiclePrefillDetails = async () => {
    try {
      const res = await fetch(
        `${config.API_BASE_URL}getUsedVehiclePrefillDetails`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: encryptData({ lead_id: leadId }),
        }
      );

      const result = await res.json();
      console.log('📦 Prefill API Response:', result);

      if (result && result.success && result.data && Object.keys(result.data).length > 0) {
        setformUsedData(prevState => ({
          ...prevState,
          rc_number: result.data.rc_number || prevState.rc_number,
          chassis_number: result.data.chassis_number || prevState.chassis_number,
          engine_number: result.data.engine_number || prevState.engine_number,
          previous_loan_id: result.data.previous_loan_id || prevState.previous_loan_id,
        }));
      }
    } catch (error) {
      console.error('❌ Error fetching vehicle prefill details:', error);
    }
  };

  // Helper function to format date for HTML date input (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    // If date is in DATETIME format (2024-01-15 10:30:00), extract just the date
    // If date is already in DATE format (2024-01-15), return as is
    const datePart = dateString.split(' ')[0];
    return datePart;
  };

  // Just capture newly selected image files locally; actual upload happens on Save
  const handleImageFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setSelectedImageFiles(prev => [...prev, ...files]);
    }
    e.target.value = '';
  };

  const handleRemoveSelectedFile = (indexToRemove) => {
    setSelectedImageFiles(prev => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleRemoveSavedImage = (indexToRemove) => {
    setformUsedData(prevState => ({
      ...prevState,
      vehicle_images: prevState.vehicle_images.filter((_, idx) => idx !== indexToRemove)
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setformUsedData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmitUsed = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    console.log('===== FORM SUBMISSION STARTED =====');
    console.log('🔄 Mode:', formUsedData.used_vehicle_id ? 'UPDATE' : 'CREATE');
    console.log('📝 used_vehicle_id:', formUsedData.used_vehicle_id);
    console.log('🔑 leadId from props:', leadId);
    console.log('🔑 formUsedData.lead_id:', formUsedData.lead_id);

    setIsSaving(true);

    try {
      // Prepare data for submission
      const submitData = {
        ...formUsedData,
        lead_id: leadId || formUsedData.lead_id, // Use prop first, fallback to formUsedData
        // Ensure chassis and engine numbers are uppercase (with null checks)
        chassis_number: formUsedData.chassis_number ? formUsedData.chassis_number.trim().toUpperCase() : '',
        engine_number: formUsedData.engine_number ? formUsedData.engine_number.trim().toUpperCase() : '',
        rc_number: formUsedData.rc_number ? formUsedData.rc_number.trim() : ''
      };

      console.log('📤 Final submitData:', submitData);
      console.log('🔑 Final lead_id being sent:', submitData.lead_id);
      console.log('🆔 used_vehicle_id being sent:', submitData.used_vehicle_id);

      const params = encryptData(submitData);

      console.log('🔐 Encrypted params prepared');
      console.log('🌐 API URL:', `${config.API_BASE_URL}create_used_vehicle`);

      let response;
      if (selectedImageFiles.length > 0) {
        // New images selected: send plain FormData fields (not encrypted) + files in the same request
        const formData = new FormData();
        Object.keys(submitData).forEach((key) => {
          const val = submitData[key];
          if (val !== null && typeof val === 'object') {
            formData.append(key, JSON.stringify(val));
          } else if (val !== null && val !== undefined) {
            formData.append(key, val);
          }
        });
        selectedImageFiles.forEach((file) => {
          formData.append('vehicle_images_files[]', file);
        });

        const rawRes = await fetch(`${config.API_BASE_URL}create_used_vehicle`, {
          method: "POST",
          body: formData
        });
        response = { data: await rawRes.json() };
      } else {
        response = await agent.post('create_used_vehicle', params);
      }

      console.log('📥 API Response:', response.data);

      // Handle response
      if (response.data && response.data.success) {
        const isUpdate = Boolean(formUsedData.used_vehicle_id);

        // Success message based on operation type
        const defaultMsg = isUpdate
          ? 'Used vehicle details updated successfully!'
          : 'Used vehicle details created successfully!';
        const successMsg = response.data.message || defaultMsg;

        toast.success(successMsg, {
          position: "top-right",
          autoClose: 3000
        });

        console.log('✅ Save successful!', isUpdate ? 'Record UPDATED' : 'Record CREATED');

        // Update used_vehicle_id if newly created
        if (response.data.data && response.data.data.used_vehicle_id) {
          console.log('🆔 Setting used_vehicle_id:', response.data.data.used_vehicle_id);
          setformUsedData(prev => ({
            ...prev,
            used_vehicle_id: response.data.data.used_vehicle_id
          }));
          setIsExistingRecord(true);
        }

        // Clear locally-selected files since they are now uploaded and saved
        setSelectedImageFiles([]);

        // Reload data from database to ensure everything is in sync
        console.log('🔄 Reloading data from database...');
        await fetchUsedVehicleData();
      } else {
        // Error from API
        const errorMsg = response.data.message || 'Failed to save used vehicle details. Please try again.';
        console.log('API Error:', errorMsg);
        toast.error(errorMsg, {
          position: "top-right",
          autoClose: 5000
        });
      }
    } catch (error) {
      console.error('Error saving used vehicle:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        request: error.request
      });

      // More detailed error messages
      let errorMessage = 'An error occurred while saving used vehicle details.';

      if (error.response) {
        console.log('Server error status:', error.response.status);
        console.log('Server error data:', error.response.data);
        // Server responded with error
        if (error.response.status === 400) {
          errorMessage = error.response.data.message || 'Invalid data provided. Please check all required fields.';
        } else if (error.response.status === 401) {
          errorMessage = 'Session expired. Please login again.';
        } else if (error.response.status === 500) {
          errorMessage = 'Server error occurred. Please contact support.';
        } else {
          errorMessage = error.response.data.message || 'Failed to save data. Please try again.';
        }
      } else if (error.request) {
        // Request made but no response
        console.log('Network error - no response received');
        errorMessage = 'Network error. Please check your internet connection.';
      }

      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000
      });
    } finally {
      console.log('Form submission finished');
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Show warning if leadId is missing
  if (!leadId) {
    return (
      <div className="used-vehicle-page">
        <div className="card">
          <div className="card-header bg-danger text-white">
            <h5 className="mb-0">Error: Lead ID Missing</h5>
          </div>
          <div className="card-body">
            <div className="alert alert-danger">
              <strong>Error:</strong> Lead ID is not available. Please ensure you are accessing this page from a valid lead edit page.
              <br />
              <small>Debug Info: leadId prop = "{leadId}"</small>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="used-vehicle-page">
      <div className="card">
        <div className="card-header bg-primary text-white">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0">Used Vehicle Details</h5>
              <small className="text-white-50">Lead ID: {leadId}</small>
            </div>
            {isExistingRecord && (
              <div>
                <span className="badge bg-success">
                  <i className="bi bi-pencil-square me-1" />
                  Editing Existing Record
                </span>
              </div>
            )}
            {!isExistingRecord && formUsedData.rc_number && (
              <div>
                <span className="badge bg-info">
                  <i className="bi bi-plus-circle me-1" />
                  Creating New Record
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="card-body">
          <div className="used-vehicle-form">

            {/* Vehicle Details Section */}
            <div className="section-title mb-3">
              <h6 className="fw-bold text-primary border-bottom pb-2">
                1. Vehicle Details
              </h6>
              <p className="small text-muted">
                You can re-punch/update vehicle details. The existing/old Loan ID should be closed before the new vehicle details are captured.
              </p>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">RC Number <span className="text-danger" style={{float: "left"}}>*</span></label>
                <input
                  type="text"
                  name="rc_number"
                  className="form-control"
                  value={formUsedData.rc_number}
                  onChange={handleInputChange}
                  placeholder="Enter RC Number"
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Chassis Number <span className="text-danger" style={{float: "left"}}>*</span></label>
                <input
                  type="text"
                  name="chassis_number"
                  className="form-control"
                  value={formUsedData.chassis_number}
                  onChange={handleInputChange}
                  placeholder="Enter Chassis Number"
                  style={{ textTransform: 'uppercase' }}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Engine Number <span className="text-danger" style={{float: "left"}}>*</span></label>
                <input
                  type="text"
                  name="engine_number"
                  className="form-control"
                  value={formUsedData.engine_number}
                  onChange={handleInputChange}
                  placeholder="Enter Engine Number"
                  style={{ textTransform: 'uppercase' }}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Previous Loan ID (if any)
                </label>
                <input
                  type="text"
                  name="previous_loan_id"
                  className="form-control"
                  value={formUsedData.previous_loan_id}
                  onChange={handleInputChange}
                  placeholder="Enter Previous Loan ID to be closed"
                />
                <small className="text-muted">Old Loan ID that will be closed</small>
              </div>
            </div>

            {/* Price Details Section */}
            <div className="section-title mb-3 mt-4">
              <h6 className="fw-bold text-primary border-bottom pb-2">
                2. Price Details
              </h6>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Price of the Vehicle <span className="text-danger" style={{float: "left"}}>*</span></label>
                <input
                  type="number"
                  name="price_of_vehicle"
                  className="form-control"
                  value={formUsedData.price_of_vehicle}
                  onChange={handleInputChange}
                  placeholder="Enter Price of Vehicle"
                  step="0.01"
                />
                <small className="text-muted">Replaces "On Road Price" field</small>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Valuation of the Asset <span className="text-danger" style={{float: "left"}}>*</span></label>
                <input
                  type="number"
                  name="valuation_of_asset"
                  className="form-control"
                  value={formUsedData.valuation_of_asset}
                  onChange={handleInputChange}
                  placeholder="Enter Asset Valuation"
                  step="0.01"
                />
              </div>
            </div>

            {/* Insurance Details Section */}
            <div className="section-title mb-3 mt-4">
              <h6 className="fw-bold text-primary border-bottom pb-2">
                3. Insurance Details
              </h6>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Insurance Company
                </label>
                <input
                  type="text"
                  name="insurance_company"
                  className="form-control"
                  value={formUsedData.insurance_company}
                  onChange={handleInputChange}
                  placeholder="Enter Insurance Company Name"
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Insurance Validity
                </label>
                <input
                  type="date"
                  name="insurance_validity"
                  className="form-control"
                  value={formUsedData.insurance_validity}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* Vehicle Age Section */}
            <div className="section-title mb-3 mt-4">
              <h6 className="fw-bold text-primary border-bottom pb-2">
                4. Vehicle Age
              </h6>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Asset Age (Years) <span className="text-danger" style={{float: "left"}}>*</span></label>
                <input
                  type="number"
                  name="asset_age"
                  className="form-control"
                  value={formUsedData.asset_age}
                  onChange={handleInputChange}
                  placeholder="Enter age in years"
                  min="0"
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Asset Age (Months)
                </label>
                <input
                  type="number"
                  name="asset_age_months"
                  className="form-control"
                  value={formUsedData.asset_age_months}
                  onChange={handleInputChange}
                  placeholder="Enter additional months"
                  
                />
              </div>
            </div>

            {/* Vehicle Running Section */}
            <div className="section-title mb-3 mt-4">
              <h6 className="fw-bold text-primary border-bottom pb-2">
                5. Vehicle Running
              </h6>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Vehicle Run / Kilometers <span className="text-danger" style={{float: "left"}}  >*</span></label>
                <input
                  type="number"
                  name="vehicle_run_kilometers"
                  className="form-control"
                  value={formUsedData.vehicle_run_kilometers}
                  onChange={handleInputChange}
                  placeholder="Enter current odometer reading"
                  step="0.01"
                />
                <small className="text-muted">Current mileage/odometer reading</small>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Vehicle Condition
                </label>
                <select
                  name="vehicle_condition"
                  className="form-select"
                  value={formUsedData.vehicle_condition}
                  onChange={handleInputChange}
                >
                  {conditionOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Vehicle Valuation/Inspection Section */}
            <div className="section-title mb-3 mt-4">
              <h6 className="fw-bold text-primary border-bottom pb-2">
                6. Vehicle Valuation/Inspection
              </h6>
              <p className="small text-muted">
                Sales Team should be able to upload vehicle images as part of the inspection/valuation process.
              </p>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Inspection Date
                </label>
                <input
                  type="date"
                  name="inspection_date"
                  className="form-control"
                  value={formUsedData.inspection_date}
                  onChange={handleInputChange}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Inspection Status
                </label>
                <select
                  name="inspection_status"
                  className="form-select"
                  value={formUsedData.inspection_status}
                  onChange={handleInputChange}
                >
                  {inspectionStatusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-12 mb-3">
                <label className="form-label">
                  Vehicle Images
                </label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  multiple
                  onChange={handleImageFilesSelected}
                />
                <small className="text-muted">
                  Upload multiple images of the vehicle for inspection/valuation. Images will be uploaded when you click Save.
                </small>

                {formUsedData.vehicle_images && formUsedData.vehicle_images.length > 0 && (
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    {formUsedData.vehicle_images.map((imgUrl, idx) => (
                      <div key={`saved-${idx}`} style={{ position: 'relative', width: '100px', height: '100px' }}>
                        <img
                          src={imgUrl}
                          alt={`Vehicle ${idx + 1}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px', border: '1px solid #dee2e6' }}
                        />
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          style={{ position: 'absolute', top: '-8px', right: '-8px', borderRadius: '50%', padding: '0px 6px', lineHeight: '20px' }}
                          onClick={() => handleRemoveSavedImage(idx)}
                          title="Remove"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {selectedImageFiles.length > 0 && (
                  <div className="mt-3">
                    <small className="text-muted d-block mb-1">New (will upload on Save):</small>
                    <div className="d-flex flex-wrap gap-2">
                      {selectedImageFiles.map((file, idx) => (
                        <div key={`new-${idx}`} style={{ position: 'relative', width: '100px', height: '100px' }}>
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`New ${idx + 1}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px', border: '1px solid #0d6efd' }}
                          />
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            style={{ position: 'absolute', top: '-8px', right: '-8px', borderRadius: '50%', padding: '0px 6px', lineHeight: '20px' }}
                            onClick={() => handleRemoveSelectedFile(idx)}
                            title="Remove"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Source Section */}
            <div className="section-title mb-3 mt-4">
              <h6 className="fw-bold text-primary border-bottom pb-2">
                7. Source
              </h6>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Source By
                </label>
                <select
                  name="source_by"
                  className="form-select"
                  value={formUsedData.source_by}
                  onChange={handleInputChange}
                >
                  <option value="">Select Source</option>
                  {sourceByOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Payment To Section */}
            <div className="section-title mb-3 mt-4">
              <h6 className="fw-bold text-primary border-bottom pb-2">
                8. Payment To
              </h6>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Payment To
                </label>
                <select
                  name="payment_to"
                  className="form-select"
                  value={formUsedData.payment_to}
                  onChange={handleInputChange}
                >
                  <option value="">Select Payment To</option>
                  {paymentToOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <small className="text-muted">
                  To whom the payment is being made
                </small>
              </div>
            </div>

            {/* Remarks Section */}
            <div className="section-title mb-3 mt-4">
              <h6 className="fw-bold text-primary border-bottom pb-2">
                9. Additional Information
              </h6>
            </div>

            <div className="row">
              <div className="col-md-12 mb-3">
                <label className="form-label">
                  Remarks
                </label>
                <textarea
                  name="remarks"
                  className="form-control"
                  rows="4"
                  value={formUsedData.remarks}
                  onChange={handleInputChange}
                  placeholder="Enter any additional remarks or notes"
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="row">
              <div className="col-md-12 mt-3">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmitUsed}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      {isExistingRecord ? 'Updating...' : 'Saving...'}
                    </>
                  ) : (
                    <>
                      <i className={`bi ${isExistingRecord ? 'bi-arrow-repeat' : 'bi-save'} me-2`} />
                      {isExistingRecord ? 'Update Used Vehicle Details' : 'Save Used Vehicle Details'}
                    </>
                  )}
                </button>
                {isExistingRecord && (
                  <small className="text-muted ms-3">
                    <i className="bi bi-info-circle me-1" />
                    This will update the existing record
                  </small>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default UsedVehiclePage;
